import { VaultSDK } from '@cef-ai/vault-sdk';
import type { CereWallet } from '@/lib/clients/cere-wallet/connect';
import { gatewayBase } from '@/lib/clients/ddc/gateway-auth';

export interface SigningSdkConfig {
  vaultBaseUrl: string;
  /**
   * S3 gateway base URL (e.g. `https://ddc-s3-gateway.compute.<env>.cere.nz`).
   * Required: vault-sdk derives the gateway base for the HTTP-only onboarding
   * check from `s3GatewayAuthInfoUrl` on the SDK config, and DEFAULTS to the
   * DEV gateway when it is unset — so omitting it would silently aim the
   * onboarding probe at dev. We derive `${s3Endpoint}/auth/info` (the path
   * the SDK strips back to the gateway base).
   */
  s3Endpoint: string;
  garUrl?: string;
  /**
   * Cere node WS URL — `chainUrl` on `VaultSDKConfig`. Required on
   * `vault.ensure()`'s default (tokenless) path: the SDK grants vault-api
   * durable write delegation by submitting an on-chain NonTransfer proxy
   * before claiming. The SDK itself errors, naming the fix, when it is
   * missing, so this stays optional only to keep read-only consumers of this
   * factory compiling.
   */
  chainUrl?: string;
}

/**
 * Build the wallet-signing VaultSDK instance — the one that performs discrete
 * signing ops (vault.ensure, agents.connect GAR agreement, scope mutations).
 * This is the ONLY instance that signs in the vault dashboard: data-plane reads
 * do not go through a signer at all, they use the Bearer instance from
 * `./read-client.ts` (`mintVaultReadClient` -> `DelegationAuthProvider`). The
 * direct-sign `createVaultBrowserClient` is not part of that split any more —
 * its only remaining caller is `src/routes/phase0-gate.tsx`.
 * session.vaultHandle / session.sdk must come from THIS, because install +
 * scope surfaces call vaultHandle.agents.connect / scopes.* app-wide.
 */
export function buildSigningSdk(
  // `Pick<..., 'signingSigner'>`, matching `browser-client.ts` and
  // `read-client.ts`: this reads exactly one member, and demanding the whole
  // `CereWallet` made every caller and fixture carry unrelated members
  // (`ceremonySurface`, `hasKeys`, `requestDelegation`) that this function
  // cannot use.
  // The `signer` XOR `auth` split is unaffected — it still receives only the
  // signing signer, never a delegation.
  wallet: Pick<CereWallet, 'signingSigner'>,
  cfg: SigningSdkConfig,
): VaultSDK {
  return new VaultSDK({
    endpoint: cfg.vaultBaseUrl,
    signer: wallet.signingSigner,
    s3GatewayAuthInfoUrl: `${gatewayBase(cfg.s3Endpoint)}/auth/info`,
    ...(cfg.garUrl ? { garEndpoint: cfg.garUrl } : {}),
    ...(cfg.chainUrl ? { chainUrl: cfg.chainUrl } : {}),
  });
}
